import { useEffect, useState } from 'react';
import { useStore, useClockSkew } from '../state/store';
import { api } from '../lib/api';

type Report = Awaited<ReturnType<typeof api.showReport>>;

/**
 * What the rig did, for afterwards.
 *
 * The questions this answers get asked in the production meeting the next
 * morning -- did anything actually drop out, which pack was marginal, was that
 * buzz on four real -- and nobody can answer them from memory. The evidence had
 * to be collected while everyone was busy, which is why the server writes it
 * down rather than whichever browser happened to be open.
 */
export default function ShowReport() {
  const identity = useStore(s => s.identity)!;
  // Server time, as everywhere else that compares against a server stamp:
  // e.at was written by the server, and a laptop running behind it would
  // otherwise render an open incident as "still going -3s".
  const skew = useClockSkew();
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.showReport(identity.code)
      .then(r => { if (alive) setReport(r); })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [identity.code]);

  if (error) return <p className="muted">{error}</p>;
  if (!report) return <p className="muted">Reading the show back…</p>;

  const clean = report.totals.incidents === 0 && report.totals.dropouts === 0;

  return (
    <div className="report">
      <div className="report-totals">
        <Stat n={report.totals.incidents}
              label={report.totals.incidents === 1 ? 'incident' : 'incidents'}
              bad={report.totals.incidents > 0} />
        <Stat n={report.totals.critical} label="serious" bad={report.totals.critical > 0} />
        <Stat n={report.totals.dropouts}
              label={report.totals.dropouts === 1 ? 'dropout' : 'dropouts'}
              bad={report.totals.dropouts > 0} />
      </div>

      {clean && (
        <p className="report-clean">
          Nothing went wrong that we saw. Every channel stayed up, no pack ran
          down, and nothing dropped out.
        </p>
      )}

      <h4>By transmitter</h4>
      <table className="report-table">
        <thead>
          <tr>
            <th>TX</th><th>Readings</th><th>Dropouts</th>
            <th>Worst RF</th><th>Lowest batt</th><th>Antenna A / B</th>
          </tr>
        </thead>
        <tbody>
          {report.channels.map(c => (
            <tr key={c.number} className={`w-${c.worst}`}>
              <td>
                <strong>{c.number}</strong>
                {c.label && <span className="muted"> {c.label}</span>}
              </td>
              {/* A channel nothing ever reported on is worth seeing as a blank
                  rather than as a row of confident zeroes. */}
              <td>{c.samples || <span className="muted">none</span>}</td>
              <td>{c.dropouts || '—'}</td>
              <td>{c.minRfLevel !== undefined ? `${c.minRfLevel} dBm` : '—'}</td>
              <td>{c.minBatteryMinutes !== undefined ? `${c.minBatteryMinutes} min` : '—'}</td>
              {/* One aerial doing all the work is the quietest serious fault
                  in this table, so it does not get to look like a statistic. */}
              <td className={lopsided(c.antennaA, c.antennaB) ? 'report-bad' : undefined}>
                {c.antennaA + c.antennaB > 0
                  ? `${c.antennaA} / ${c.antennaB}`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {report.events.length > 0 && (
        <>
          <h4>What happened, and when</h4>
          <ul className="report-events">
            {report.events.map(e => (
              <li key={e.id} className={e.severity}>
                <span className="report-when">{clockOf(e.at)}</span>
                <span className="report-what">{e.message}</span>
                <span className="report-for">
                  {/* "at the end" is wrong while the show is still running,
                      which is when this gets opened most. */}
                  {e.clearedAt
                    ? `lasted ${duration(e.clearedAt - e.at)}`
                    : `still going · ${duration(Math.max(0, Date.now() + skew - e.at))}`}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** Matches the alert threshold: under a tenth of the work is not diversity. */
function lopsided(a: number, b: number): boolean {
  const total = a + b;
  return total > 0 && Math.min(a, b) / total < 0.1;
}

function Stat({ n, label, bad }: { n: number; label: string; bad: boolean }) {
  return (
    <div className={`report-stat${bad ? ' bad' : ''}`}>
      <strong>{n}</strong>
      <span>{label}</span>
    </div>
  );
}

/*
 * 24-hour, like every other time in the app and like every running order ever
 * printed. toLocaleTimeString gave "10:59 PM" next to a call sheet reading
 * 19:00-21:30, which is a needless second of translation at the one moment
 * somebody is trying to line up an incident against a cue.
 */
const clockOf = (ms: number) => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Short enough to scan down a column. */
function duration(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)}h ${min % 60}m`;
}
